import { useState, useCallback } from 'react';
import type { RenderedVideo } from '@/types/video-enhancer';
import { CreditOperation } from '@/hooks/useCredits';

const globalCompletedRemovalJobs = new Set<string>();
const globalCompletedCompositeJobs = new Set<string>();
const globalCompletedGreenScreenJobs = new Set<string>();

export type WizardStep = 'removing' | 'choose' | 'select-background';
export type BackgroundSourceTab = 'generate' | 'project' | 'upload';

interface UseBackgroundReplacementParams {
  renderedVideos: RenderedVideo[];
  setRenderedVideos: React.Dispatch<React.SetStateAction<RenderedVideo[]>>;
  toast: (opts: { title: string; description?: string; variant?: string }) => void;
  requireCreditConfirmation: (
    operationType: CreditOperation,
    operationName: string,
    onConfirm: () => void
  ) => void;
  CREDIT_OPERATIONS: {
    BACKGROUND_REMOVAL: CreditOperation;
    COMPOSITE_BACKGROUND: CreditOperation;
    IMAGE_GENERATION: CreditOperation;
    VIDEO_GENERATION: CreditOperation;
  };
}

export function useBackgroundReplacement({
  renderedVideos,
  setRenderedVideos,
  toast,
  requireCreditConfirmation,
  CREDIT_OPERATIONS,
}: UseBackgroundReplacementParams) {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState<WizardStep>('removing');
  const [targetVideo, setTargetVideo] = useState<RenderedVideo | null>(null);
  const [maskUrl, setMaskUrl] = useState<string | null>(null);
  const [maskKey, setMaskKey] = useState<string | null>(null);

  // Output type selection dialog state
  const [outputTypeDialogOpen, setOutputTypeDialogOpen] = useState(false);
  const [selectedOutputType, setSelectedOutputType] = useState<'foreground-mask' | 'green-screen'>('foreground-mask');
  const [pendingVideo, setPendingVideo] = useState<RenderedVideo | null>(null);

  const openWizard = useCallback((video: RenderedVideo) => {
    setTargetVideo(video);
    setMaskUrl(null);
    setMaskKey(null);
    setWizardStep('removing');
    setWizardOpen(true);
  }, []);

  const closeWizard = useCallback(() => {
    setWizardOpen(false);
    setTargetVideo(null);
    setMaskUrl(null);
    setMaskKey(null);
  }, []);

  // Step 1: Start background removal via Replicate
  const startBackgroundRemoval = useCallback(async (video: RenderedVideo, outputType: 'foreground-mask' | 'green-screen' = 'foreground-mask') => {
    const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
    const videoUrl = video.videoUrl || video.url || video.savedS3Url;

    if (!videoUrl) {
      toast({
        title: 'No video URL',
        description: 'Cannot process this video - no URL available',
        variant: 'destructive',
      });
      closeWizard();
      return;
    }

    try {
      // Update video state
      setRenderedVideos(prev => prev.map(v =>
        v.id === video.id
          ? { ...v, bgRemovalStatus: 'processing' as const, bgRemovalProgress: 0 }
          : v
      ));

      const response = await fetch('/api/remove-video-background', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          videoUrl,
          outputType,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Background removal failed');
      }

      const result = await response.json();

      if (result.jobId) {
        setRenderedVideos(prev => prev.map(v =>
          v.id === video.id
            ? { ...v, bgRemovalJobId: result.jobId, bgRemovalStatus: 'processing' as const }
            : v
        ));

        if (outputType === 'green-screen') {
          // Green screen: poll and save result directly (no wizard "choose" step)
          pollGreenScreenStatus(result.jobId, video.id);
        } else {
          // Foreground mask: poll and then show wizard "choose" step
          pollBackgroundRemoval(result.jobId, video.id);
        }
      }
    } catch (error: any) {
      console.error('Background removal error:', error);
      setRenderedVideos(prev => prev.map(v =>
        v.id === video.id
          ? { ...v, bgRemovalStatus: 'failed' as const, bgRemovalJobId: undefined }
          : v
      ));
      toast({
        title: 'Background removal failed',
        description: error.message || 'Failed to start background removal',
        variant: 'destructive',
      });
      closeWizard();
    }
  }, [setRenderedVideos, toast, closeWizard]);

  const pollBackgroundRemoval = useCallback((jobId: string, videoId: string) => {
    let attempts = 0;
    const maxAttempts = 240; // 20 minutes
    let consecutiveErrors = 0;

    const pollInterval = setInterval(async () => {
      try {
        attempts++;
        const response = await fetch(`/api/remove-video-background/status/${jobId}`);
        const result = await response.json();
        consecutiveErrors = 0;

        if (result.status === 'completed') {
          clearInterval(pollInterval);

          if (globalCompletedRemovalJobs.has(jobId)) return;
          globalCompletedRemovalJobs.add(jobId);

          const resolvedUrl = result.videoUrl?.startsWith('/api/s3/')
            ? `${window.location.origin}${result.videoUrl}`
            : result.videoUrl;

          setMaskUrl(resolvedUrl);
          setMaskKey(result.s3Key);

          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId
              ? {
                  ...v,
                  bgRemovalStatus: 'completed' as const,
                  bgRemovalProgress: 100,
                  bgMaskUrl: resolvedUrl,
                  bgMaskKey: result.s3Key,
                  bgRemovalJobId: undefined,
                }
              : v
          ));

          // Advance wizard to choose step
          setWizardStep('choose');

        } else if (result.status === 'failed' || result.status === 'expired') {
          clearInterval(pollInterval);
          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId
              ? { ...v, bgRemovalStatus: 'failed' as const, bgRemovalJobId: undefined }
              : v
          ));
          toast({
            title: 'Background removal failed',
            description: result.error || 'Processing failed',
            variant: 'destructive',
          });
          closeWizard();

        } else {
          // Still processing - estimate progress
          const progress = Math.min(90, Math.round((attempts / maxAttempts) * 100));
          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId
              ? { ...v, bgRemovalProgress: progress }
              : v
          ));
        }

        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId
              ? { ...v, bgRemovalStatus: 'failed' as const, bgRemovalJobId: undefined }
              : v
          ));
          toast({
            title: 'Background removal timed out',
            description: 'The operation took longer than expected',
            variant: 'destructive',
          });
          closeWizard();
        }
      } catch (error) {
        console.error('BG removal polling error:', error);
        consecutiveErrors++;
        if (consecutiveErrors >= 5) {
          clearInterval(pollInterval);
          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId
              ? { ...v, bgRemovalStatus: 'failed' as const, bgRemovalJobId: undefined }
              : v
          ));
          toast({
            title: 'Background removal failed',
            description: 'Lost connection to processing service',
            variant: 'destructive',
          });
          closeWizard();
        }
      }
    }, 5000);

    // 30 minute safety timeout
    setTimeout(() => clearInterval(pollInterval), 1800000);
  }, [setRenderedVideos, toast, closeWizard]);

  // Keep green screen: send to Replicate with green-screen output type
  const keepGreenScreen = useCallback(async () => {
    if (!targetVideo) {
      closeWizard();
      return;
    }

    const videoUrl = targetVideo.videoUrl || targetVideo.url || targetVideo.savedS3Url;
    if (!videoUrl) {
      toast({
        title: 'No video URL',
        description: 'Original video URL not available',
        variant: 'destructive',
      });
      closeWizard();
      return;
    }

    const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');

    try {
      setRenderedVideos(prev => prev.map(v =>
        v.id === targetVideo.id
          ? { ...v, bgCompositeStatus: 'processing' as const }
          : v
      ));

      toast({
        title: 'Creating green screen video',
        description: 'Processing with Replicate...',
      });

      const response = await fetch('/api/remove-video-background', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          videoUrl,
          outputType: 'green-screen',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Green screen creation failed');
      }

      const result = await response.json();

      if (result.jobId) {
        pollGreenScreenStatus(result.jobId, targetVideo.id);
      }
    } catch (error: any) {
      console.error('Green screen error:', error);
      setRenderedVideos(prev => prev.map(v =>
        v.id === targetVideo.id
          ? { ...v, bgCompositeStatus: 'failed' as const }
          : v
      ));
      toast({
        title: 'Green screen creation failed',
        description: error.message || 'Failed to create green screen video',
        variant: 'destructive',
      });
      closeWizard();
    }
  }, [targetVideo, setRenderedVideos, toast, closeWizard]);

  // Poll for green screen job completion (uses same status endpoint as BG removal)
  const pollGreenScreenStatus = useCallback((jobId: string, videoId: string) => {
    let attempts = 0;
    const maxAttempts = 240;
    let consecutiveErrors = 0;

    const pollInterval = setInterval(async () => {
      try {
        attempts++;
        const response = await fetch(`/api/remove-video-background/status/${jobId}`);
        const result = await response.json();
        consecutiveErrors = 0;

        if (result.status === 'completed') {
          clearInterval(pollInterval);

          if (globalCompletedGreenScreenJobs.has(jobId)) return;
          globalCompletedGreenScreenJobs.add(jobId);

          const resolvedUrl = result.videoUrl?.startsWith('/api/s3/')
            ? `${window.location.origin}${result.videoUrl}`
            : result.videoUrl;

          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId
              ? {
                  ...v,
                  bgCompositeStatus: 'completed' as const,
                  bgReplacedUrl: resolvedUrl,
                }
              : v
          ));

          toast({
            title: 'Green screen video ready!',
            description: 'Your video with green background is available',
          });

          closeWizard();

        } else if (result.status === 'failed' || result.status === 'expired') {
          clearInterval(pollInterval);
          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId
              ? { ...v, bgCompositeStatus: 'failed' as const }
              : v
          ));
          toast({
            title: 'Green screen creation failed',
            description: result.error || 'Processing failed',
            variant: 'destructive',
          });
          closeWizard();
        }

        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId
              ? { ...v, bgCompositeStatus: 'failed' as const }
              : v
          ));
          toast({
            title: 'Green screen timed out',
            description: 'The operation took longer than expected',
            variant: 'destructive',
          });
          closeWizard();
        }
      } catch (error) {
        console.error('Green screen polling error:', error);
        consecutiveErrors++;
        if (consecutiveErrors >= 5) {
          clearInterval(pollInterval);
          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId
              ? { ...v, bgCompositeStatus: 'failed' as const }
              : v
          ));
          toast({
            title: 'Green screen creation failed',
            description: 'Lost connection to processing service',
            variant: 'destructive',
          });
          closeWizard();
        }
      }
    }, 5000);

    setTimeout(() => clearInterval(pollInterval), 1800000);
  }, [setRenderedVideos, toast, closeWizard]);

  // Composite: combine foreground with new background
  const compositeBackground = useCallback(async (
    backgroundUrl: string,
    backgroundType: 'image' | 'video'
  ) => {
    if (!targetVideo || !maskUrl) {
      toast({
        title: 'Missing data',
        description: 'Background mask not available',
        variant: 'destructive',
      });
      return;
    }

    const originalVideoUrl = targetVideo.videoUrl || targetVideo.url || targetVideo.savedS3Url;
    if (!originalVideoUrl) {
      toast({
        title: 'No video URL',
        description: 'Original video URL not available',
        variant: 'destructive',
      });
      return;
    }

    const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');

    try {
      setRenderedVideos(prev => prev.map(v =>
        v.id === targetVideo.id
          ? { ...v, bgCompositeStatus: 'processing' as const }
          : v
      ));

      const response = await fetch('/api/composite-background', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          originalVideoUrl,
          alphaMaskUrl: maskUrl,
          backgroundUrl,
          backgroundType,
          duration: targetVideo.duration,
          outputName: `${targetVideo.name} (BG Replaced)`,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || 'Composition failed');
      }

      const result = await response.json();

      if (result.renderId) {
        setRenderedVideos(prev => prev.map(v =>
          v.id === targetVideo.id
            ? { ...v, bgCompositeRenderId: result.renderId }
            : v
        ));

        toast({
          title: 'Compositing started',
          description: `Combining video with new background...`,
        });

        pollCompositeStatus(result.renderId, targetVideo.id);
      }
    } catch (error: any) {
      console.error('Composite error:', error);
      setRenderedVideos(prev => prev.map(v =>
        v.id === targetVideo.id
          ? { ...v, bgCompositeStatus: 'failed' as const }
          : v
      ));
      toast({
        title: 'Composition failed',
        description: error.message || 'Failed to start composition',
        variant: 'destructive',
      });
    }
  }, [targetVideo, maskUrl, setRenderedVideos, toast]);

  const executeCompositeBackground = useCallback((
    backgroundUrl: string,
    backgroundType: 'image' | 'video'
  ) => {
    requireCreditConfirmation(
      CREDIT_OPERATIONS.COMPOSITE_BACKGROUND,
      'Background Replacement',
      () => compositeBackground(backgroundUrl, backgroundType)
    );
  }, [requireCreditConfirmation, CREDIT_OPERATIONS, compositeBackground]);

  const pollCompositeStatus = useCallback((renderId: string, videoId: string) => {
    let attempts = 0;
    const maxAttempts = 120;

    const pollInterval = setInterval(async () => {
      try {
        attempts++;
        const response = await fetch(`/api/composite-background/status/${renderId}`);
        const result = await response.json();

        if (result.status === 'completed' && result.videoUrl) {
          clearInterval(pollInterval);

          if (globalCompletedCompositeJobs.has(renderId)) return;
          globalCompletedCompositeJobs.add(renderId);

          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId
              ? {
                  ...v,
                  bgCompositeStatus: 'completed' as const,
                  bgReplacedUrl: result.videoUrl,
                  bgCompositeRenderId: undefined,
                }
              : v
          ));

          const video = renderedVideos.find(v => v.id === videoId);
          toast({
            title: 'Background replaced!',
            description: `New version ready for ${video?.name || 'your video'}`,
          });

          closeWizard();

        } else if (result.status === 'failed') {
          clearInterval(pollInterval);
          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId
              ? { ...v, bgCompositeStatus: 'failed' as const, bgCompositeRenderId: undefined }
              : v
          ));
          toast({
            title: 'Composition failed',
            description: result.error || 'Processing failed',
            variant: 'destructive',
          });
        }

        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setRenderedVideos(prev => prev.map(v =>
            v.id === videoId
              ? { ...v, bgCompositeStatus: 'failed' as const, bgCompositeRenderId: undefined }
              : v
          ));
          toast({
            title: 'Composition timed out',
            description: 'The operation took longer than expected',
            variant: 'destructive',
          });
        }
      } catch (error) {
        console.error('Composite polling error:', error);
      }
    }, 5000);

    setTimeout(() => clearInterval(pollInterval), 900000);
  }, [setRenderedVideos, renderedVideos, toast, closeWizard]);

  // Show output type dialog before starting BG removal
  const executeRemoveBackground = useCallback((video: RenderedVideo) => {
    setPendingVideo(video);
    setSelectedOutputType('foreground-mask');
    setOutputTypeDialogOpen(true);
  }, []);

  // Called when user confirms output type selection
  const confirmRemoveBackground = useCallback(() => {
    if (!pendingVideo) return;
    const video = pendingVideo;
    const outputType = selectedOutputType;
    setOutputTypeDialogOpen(false);
    setPendingVideo(null);

    requireCreditConfirmation(
      CREDIT_OPERATIONS.BACKGROUND_REMOVAL,
      'Background Removal',
      () => {
        if (outputType === 'green-screen') {
          // Green screen: open wizard in "removing" state, single API call, no "choose" step
          openWizard(video);
          startBackgroundRemoval(video, 'green-screen');
        } else {
          // Foreground mask: open wizard, will show "choose" step after removal completes
          openWizard(video);
          startBackgroundRemoval(video, 'foreground-mask');
        }
      }
    );
  }, [pendingVideo, selectedOutputType, requireCreditConfirmation, CREDIT_OPERATIONS, openWizard, startBackgroundRemoval]);

  return {
    wizardOpen,
    wizardStep,
    setWizardStep,
    targetVideo,
    maskUrl,
    closeWizard,
    executeRemoveBackground,
    keepGreenScreen,
    executeCompositeBackground,
    // Output type dialog
    outputTypeDialogOpen,
    setOutputTypeDialogOpen,
    selectedOutputType,
    setSelectedOutputType,
    confirmRemoveBackground,
  };
}
